import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { capitalize, isNil, get } from 'lodash';
import {
  Card,
  CardHeader,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  DropdownToggle,
  UncontrolledDropdown,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { Button, ButtonLink } from 'components/Button';
import AutopayUnavailableTooltip from 'components/AutopayUnavailableTooltip';
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import {
  selectLoanId,
  fetchLoansList,
  fetchCustomerDetail,
  fetchLoanDetail,
  fetchPaymentsList,
  fetchTransactionsList,
  fetchDocuments,
  fetchLoanTerm,
  makePayment,
  fetchPaymentMethods,
  putScheduleAutopay as putScheduleAutopayMapped,
  fetchAutopayInfo,
  fetchCurrentPayoffInfo,
  fetchFuturePayoffInfo,
  putPaymentAccount,
  postPaymentAccount,
  postDebitCardAccount as postDebitCardAccountAction,
  fetchAutopays,
  fetchLoanCharges,
  getBillingSetting,
  validateBankRoutingNumber as validateBankRoutingNumberAction,
  fetchScheduledPayment,
  fetchCollateralRequired,
  fetchCollateralSummary,
  fetchNotices,
} from 'actions/dashboard';

import PaymentModal from 'components/PaymentModal';
import AddPaymentModal from 'components/AddPaymentMethods';
import TermsModal from 'components/TermsModal';
import LoansAgreementModal from 'components/LoansAgreementModal';
import RecurringPaymentModal from 'components/RecurringPaymentModal';
import DisclosureModal from 'components/DisclosureModal';
import PromoPayoffModal from 'components/PromoPayoffModal';
import Loader from 'components/Loader';
import ApplePayBanner from 'components/ApplePayBanner';
import CancelPaymentModal from 'components/CancelPaymentModal';

import { checkIfApplePayIsAvailable } from 'utils/helper';
import { formatCurrency } from 'utils/formatCurrency';
import { floatUnmask } from 'utils/masks';
import { getCurrentDate } from 'utils/func';
import { parseUrlParams } from 'utils/parseUrlParams';
import { useConfig } from 'config/ConfigContext';
import { normalizePaymentAccounts, derivePrimaryPaymentMethod } from 'utils/paymentAccountMapper';
import CollateralSummaryCard from './components/CollateralSummaryCard';
import LiquidationDetailsCard from './components/LiquidationDetailsCard';
import LowerLtvModal from './components/LowerLtvModal';
import PendingPayoffCard from './components/PendingPayoffCard';
import {
  getCollateralSummaryHeldDisplay,
  getCryptoWelcomeHeaderContent,
  formatLtvMetricDisplay,
  isDtcCryptoLoan,
  isInGracePeriod,
  isPendingPayoffLoan,
  resolveCollateralDisplayValue,
  resolvePaymentDescription,
  resolveDashboardCryptoState,
  resolveIsCryptoLoan,
  isDeficiencyBalanceLoan,
  isLiquidationSatisfiedLoan,
} from './utils/cryptoUtils';
import { resolveMaturityWarningState } from './utils/maturityUtils';
import { AUTOPAY_UNAVAILABLE_MESSAGE, isOnlineAutopayUnavailable } from './utils/autopayAvailability';

const getNoticesList = (notices) => {
  const data = get(notices, 'data');
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.notices)) {
    return data.notices;
  }
  if (data && typeof data === 'object') {
    return [data];
  }
  return [];
};

const getScraWaiverPath = (notices) => {
  const list = getNoticesList(notices);
  const scraNotice = list.find((item) => (
    `${get(item, 'NoticeType') || get(item, 'noticeType') || ''}`.toLowerCase() === 'scra-waiver'
  ));
  const attributes = get(scraNotice, 'Attributes') || get(scraNotice, 'attributes') || [];
  const urlAttribute = attributes.find((item) => (
    `${get(item, 'Key') || get(item, 'key') || ''}`.toLowerCase() === 'url'
  ));

  return get(urlAttribute, 'Value') || get(urlAttribute, 'value') || null;
};

const isScraBannerActiveForLoan = (loansList, loanId) => {
  // loansList.data may be null before the loans list has loaded; lodash `get`
  // only substitutes the default for `undefined`, so guard against null here.
  const loans = get(loansList, 'data') || [];
  const loan = loans.find(
    (item) => String(get(item, 'loanId') || '') === String(loanId || '')
  );

  if (!isDtcCryptoLoan(loan)) {
    return false;
  }
  const isScraActive = get(loan, 'exclusions.isScraActive', false);
  const isScraWaiverSigned = get(loan, 'exclusions.isScraWaiverSigned', false);
  return Boolean(isScraActive && !isScraWaiverSigned);
};

const Dashboard = (props) => {
  const { delay } = useConfig();
  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState('one-time');
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
  const [isLoanAgreementModal, setIsLoanAgreementModal] = useState(false);
  const [isRecurringPaymentModal, setIsRecurringPaymentModal] = useState(false);
  const [isAddOrEditPaymentModalVisible, setIsAddOrEditPaymentModalVisible] = useState(false);
  const [isDisclosureModalVisible, setIsDisclosureModalVisible] = useState(false);
  const [isCancelPaymentModal, setIsCancelPaymentModal] = useState(false);
  const [paymentModalType, setPaymentModalType] = useState('add');
  const [scheduledPaymentDate, setScheduledPaymentDate] = useState('');
  const [isPromoPayoffModal, setIsPromoPayoffModal] = useState(false);
  const [isLowerLtvModalOpen, setIsLowerLtvModalOpen] = useState(false);
  const [isMakePaymentLoading, setIsMakePaymentLoading] = useState(false);
  const [isAutoPayLoading, setIsAutoPayLoading] = useState(false);
  const [nextPaymentAmt, setNextPaymentAmt] = useState(0);
  const [nextPayment, setNextPayment] = useState(0);
  const [isBannerHidden, setIsBannerHidden] = useState(false);
  const [isSettlementActive, setIsSettlementActive] = useState(false);
  const isAutoPayEnabled = Boolean(get(props.autopay, 'data.isAutoPayEnabled', false));
  const lateFee = get(props.loanTerm, 'data.lateFeeAmount', '-');
  const isApplePayAvailable = checkIfApplePayIsAvailable();
  const navigate = useNavigate();
  const fetchedLoanIdRef = useRef(null);
  useEffect(() => {
    const paymentList = get(props, 'paymentsList.data') || [];
    const activePaymentList = [...props.scheduledPaymentsList, ...paymentList];

    let nextPay = {};
    nextPay = activePaymentList?.filter((item) => item && new Date(item?.applyDate || item?.date) > new Date())
      .sort((a, b) => (new Date(a.applyDate || a?.date) < new Date(b.applyDate || a?.date) ? -1 : 1))[0];

    setNextPayment(nextPay);
  }, [props.scheduledPaymentsList, props.paymentsList]);

  useEffect(() => {
    if (nextPayment) {
      setNextPaymentAmt(nextPayment.amount || 0);
    }
  }, [nextPayment]);

  const handleModal = (newTab) => {
    const { loansList } = props;

    if (newTab === 'autopay' && isOnlineAutopayUnavailable(props.loanTerm, loansList)) {
      return;
    }

    if (!props.paymentMethods.isLoading && !modal) {
      if (newTab === 'one-time') setIsMakePaymentLoading(true);
      else if (newTab === 'autopay') setIsAutoPayLoading(true);
      props.fetchLoanCharges(loansList.activeLoanId);
      props.fetchPaymentMethods({
        success: () => {
          setModal(!modal);
          setActiveTab(newTab);
          setIsMakePaymentLoading(false);
          setIsAutoPayLoading(false);
        },
        fail: () => {
          setModal(!modal);
          setActiveTab(newTab);
          setIsMakePaymentLoading(false);
          setIsAutoPayLoading(false);
        },
      });
    } else {
      setModal(!modal);
      setActiveTab('one-time');
    }
  };

  // Ensure the dashboard's own data dependencies (loans list + customer detail)
  // are loaded. Some entry paths reach the dashboard without the auth saga's
  // postAuthentication having run (e.g. enabling email/authenticator MFA, or the
  // 2FA "remind me later" skip, which navigate here directly). Without the loans
  // list, activeLoanId stays null and every loan-scoped fetch below is skipped,
  // leaving the dashboard stuck on its loader. Fetch here when missing so the
  // dashboard is self-sufficient regardless of how it was reached.
  useEffect(() => {
    // Reload the loans list only when Dashboard does not already have one.
    // Returning from Documents already has the list and the loan the customer
    // picked; refetching here was replacing that loan with the first account.
    const hasLoadedLoans = Array.isArray(props.loansList?.data) && props.loansList.data.length > 0;
    if (!hasLoadedLoans && !props.loansList?.isLoading) {
      props.fetchLoansList();
    }
    if (!props.customerDetail?.isLoading) {
      props.fetchCustomerDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { displayLoanId, autopay } = parseUrlParams(window.location.search);
    const { loansList } = props;
    let { activeLoanId } = loansList;
    const savedLoanId = localStorage.getItem('selectedLoanId');

    if (savedLoanId && Array.isArray(loansList.data)) {
      const savedLoan = loansList.data.find((loan) => String(loan.loanId) === String(savedLoanId));
      if (savedLoan && String(activeLoanId) !== String(savedLoan.loanId)) {
        props.selectLoanId(savedLoan.loanId);
        return;
      }
    }

    if (displayLoanId && autopay === 'true' && loansList.data) {
      const loan = loansList.data.find((l) => l.loanId === displayLoanId);
      if (loan) {
        activeLoanId = loan.loanId;
        props.selectLoanId(activeLoanId);
        props.fetchTransactionsList(activeLoanId);
        if (!isAutoPayEnabled && !isOnlineAutopayUnavailable(props.loanTerm, loansList)) {
          handleModal('autopay');
        }
      }
    }

    if (activeLoanId && String(fetchedLoanIdRef.current) !== String(activeLoanId)) {
      fetchedLoanIdRef.current = activeLoanId;

      props.fetchLoanTerm(activeLoanId);
      props.fetchLoanDetail({
        loanId: activeLoanId,
      });
      props.fetchPaymentsList(activeLoanId);
      props.fetchScheduledPayment(activeLoanId);
      props.fetchAutopayInfo(activeLoanId);
      props.fetchLoanCharges(activeLoanId);
      props.fetchCollateralSummary(activeLoanId);
      props.getBillingSetting(activeLoanId);
      if (isScraBannerActiveForLoan(loansList, activeLoanId)) {
        props.fetchNotices(activeLoanId);
      }
    }

    return () => {
      sessionStorage.setItem('visitedDashboard', true);
    };
  }, [props.loansList?.activeLoanId, props.loansList?.data]);

  useEffect(() => {
    if (!isOnlineAutopayUnavailable(props.loanTerm, props.loansList)) {
      return;
    }

    if (activeTab === 'autopay') {
      setActiveTab('one-time');
      setModal(false);
    }
  }, [props.loanTerm, props.loansList, activeTab]);

  useEffect(() => {
    const { loansList } = props;
    const { activeLoanId } = loansList;

    if (!activeLoanId || !resolveIsCryptoLoan(loansList)) {
      return;
    }

    props.fetchCollateralRequired(activeLoanId);
  }, [props.loansList?.activeLoanId, props.loansList?.data]);

  useEffect(() => {
    const promoPayoffModalShown = JSON.parse(sessionStorage.getItem('promoPayoffModalShown')) || [];
    if (`${props.promoEligibility}`.toLowerCase() === 'yes' && get(props.loanDetail, 'data.0.loanSubStatusId') === 50 && !promoPayoffModalShown.includes(get(props.loanDetail, 'data.0.id'))) {
      promoPayoffModalShown.push(get(props.loanDetail, 'data.0.id'));
      sessionStorage.setItem('promoPayoffModalShown', JSON.stringify(promoPayoffModalShown));
      setIsPromoPayoffModal(true);
    }
    setIsSettlementActive(!isNil(get(props.loanDetail, 'data.0.settlement')));
  }, [props.loanDetail, props.promoEligibility]);

  const handleTermsModal = () => {
    setIsTermsModalVisible(!isTermsModalVisible);
  };

  const handleLoanAgreementModal = () => {
    setIsLoanAgreementModal(!isLoanAgreementModal);
  };

  const handleRecurringModal = () => {
    setIsRecurringPaymentModal(!isRecurringPaymentModal);
  };

  const handleCancelPaymentModal = () => {
    setIsCancelPaymentModal(!isCancelPaymentModal);
  };

  const handleLowerLtvModal = () => {
    const nextOpen = !isLowerLtvModalOpen;
    if (nextOpen && props.loansList.activeLoanId) {
      props.fetchCollateralRequired(props.loansList.activeLoanId);
      props.fetchPaymentMethods({});
    }
    setIsLowerLtvModalOpen(nextOpen);
  };

  const handleClickAllPayments = (e) => {
    e.preventDefault();
    navigate('/consumer/loans', { state: { activeTab: 1 } });
  };

  const handleChange = (e) => {
    const nextLoanId = e.target.value;
    const { loansList } = props;
    const nextLoan = loansList.data?.find((loan) => loan.loanId === nextLoanId);
    const isNextCrypto = isDtcCryptoLoan(nextLoan);

    localStorage.setItem('selectedLoanId', nextLoanId);
    props.getBillingSetting(nextLoanId);
    props.selectLoanId(nextLoanId);
    props.fetchLoanDetail({
      loanId: nextLoanId,
    });
    props.fetchTransactionsList(nextLoanId);
    props.fetchLoanTerm(nextLoanId);
    props.fetchAutopayInfo(nextLoanId);
    props.fetchScheduledPayment(nextLoanId);
    props.fetchPaymentsList(nextLoanId);
    props.fetchLoanCharges(nextLoanId);
    props.fetchCollateralSummary(nextLoanId);
    if (isScraBannerActiveForLoan(loansList, nextLoanId)) {
      props.fetchNotices(nextLoanId);
    }
    if (isNextCrypto) {
      props.fetchCollateralRequired(nextLoanId);
    }
  };

  const toggleDisclosureModal = (date = null) => {
    setIsDisclosureModalVisible(!isDisclosureModalVisible);
    if (date) {
      setScheduledPaymentDate(date);
    } else {
      setScheduledPaymentDate('');
    }
  };

  const putMakePayment = (data, cb, options = {}) => {
    const { closePaymentModal = true } = options;
    const { loansList } = props;

    props.fetchAutopays({
      loanId: loansList.activeLoanId,
      success: (res1) => {
        props.makePayment({
          data,
          loanId: loansList.activeLoanId,
          success: (res) => {
            if (res && res.some((item) => get(item, 'paymentProcessor.status') === 'Failed')) {
              toast.error(
                (
                  <div>
                    <h5>We encountered an error while processing payment</h5>
                  </div>
                ),
                { autoClose: true }
              );
              if (cb) cb(false);
              return;
            }

            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', 'Payment made successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );

            const autopayArr = res1.slice();

            if (autopayArr.length) {
              autopayArr.sort((a, b) => new Date(a.processDateTime) - new Date(b.processDateTime));
              const firstAutopayAfterToday = autopayArr.find((item) => (new Date(item.processDateTime) - new Date()) > 0 && `${get(item, 'recurringFrequency.id')}`.toLowerCase() !== 'autopay.recurringfrequency.single');
              if (firstAutopayAfterToday) {
                toggleDisclosureModal(get(firstAutopayAfterToday, 'processDateTime'));
              }
            }

            if (closePaymentModal) {
              setModal(false);
            }
            props.fetchTransactionsList(loansList.activeLoanId);
            props.fetchPaymentsList(loansList.activeLoanId);
            props.fetchLoanDetail({
              loanId: loansList.activeLoanId,
              delay,
            });
            setTimeout(() => {
              props.fetchLoanCharges(loansList.activeLoanId);
            }, 15000);
            if (cb) cb(true);
          },
          fail: (err) => {
            if (err.status === 400) {
              toast.error(
                (
                  <div>
                    <h5>There was an error processing your payment</h5>
                    <small>{get(err, 'data.message')}</small>
                  </div>
                ),
                { autoClose: true }
              );
            }
            if (cb) cb(false);
          },
        });
      },
    });
  };

  const putScheduleAutopay = (data, cb, options = {}) => {
    const { closePaymentModal = true } = options;
    const { loansList } = props;
    props.fetchAutopays({
      loanId: loansList.activeLoanId,
      success: (res1) => {
        props.putScheduleAutopay({
          data,
          loanId: loansList.activeLoanId,
          success: (res) => {
            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', data.type === 'autopay.type.single' ? 'Payment scheduled successfully.' : 'AutoPay was scheduled successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );

            const autopayArr = res1.slice();

            if (autopayArr.length && data.type === 'autopay.type.single') {
              autopayArr.sort((a, b) => new Date(a.processDateTime) - new Date(b.processDateTime));
              const firstAutopayAfterToday = autopayArr.find((item) => (new Date(item.processDateTime) - new Date()) > 0 && `${get(item, 'recurringFrequency.id')}`.toLowerCase() !== 'autopay.recurringfrequency.single');
              if (firstAutopayAfterToday) {
                toggleDisclosureModal(get(firstAutopayAfterToday, 'processDateTime'));
              }
            }

            if (closePaymentModal) {
              setModal(false);
            }
            props.fetchTransactionsList(loansList.activeLoanId);
            props.fetchPaymentsList(loansList.activeLoanId);
            props.fetchLoanDetail({
              loanId: loansList.activeLoanId,
              delay,
            });
            props.fetchAutopayInfo(loansList.activeLoanId);
            setTimeout(() => {
              props.fetchLoanCharges(loansList.activeLoanId);
            }, 15000);
            if (cb) cb(true);
          },
          fail: (err) => {
            if (err.status === 400) {
              toast.error(
                (
                  <div>
                    <h5>There was an error processing your payment</h5>
                    <small>{get(err, 'data.message')}</small>
                  </div>
                ),
                { autoClose: true }
              );
            }
            if (cb) cb(false);
          },
        });
      },
      fail: (err) => {
        if (err.status === 400) {
          toast.error(
            (
              <div>
                <h5>There was an error processing your payment</h5>
                <small>{get(err, 'data.message')}</small>
              </div>
            ),
            { autoClose: true }
          );
        }
        if (cb) cb(false);
      },
    });
  };

  const handleAddPaymentModal = () => {
    setIsAddOrEditPaymentModalVisible(!isAddOrEditPaymentModalVisible);
    setPaymentModalType('add');
  };

  const savePaymentAccount = (data, cb) => {
    if (data) {
      props.postPaymentAccount({
        data,
        success: (res) => {
          if (cb) { cb(true); }
          toast.success(
            (
              <div>
                <h5>{get(res, 'data.message', 'Payment method added successfully!')}</h5>
              </div>
            ),
            { autoClose: true }
          );
          props.fetchPaymentMethods();
          setIsAddOrEditPaymentModalVisible(false);
        },
        fail: (err) => {
          if (cb) { cb(); }
          if (err.status === 400) {
            toast.error(
              (
                <div>
                  <h5>There was an error saving your payment account</h5>
                  <small>{get(err, 'data.message', 'Error occured')}</small>
                </div>
              ),
              { autoClose: true }
            );
          }
        },
      });
    }
  };

  const handlePayoffModal = () => {
    setIsPromoPayoffModal(!isPromoPayoffModal);
  };

  const saveDebitCardToken = (data, cb) => {
    if (data) {
      if (paymentModalType === 'add') {
        props.postDebitCardAccount({
          data,
          success: (res) => {
            if (cb) { cb(true); }
            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', 'Payment method added successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );
            props.fetchPaymentMethods();
            setIsAddOrEditPaymentModalVisible(false);
          },
          fail: (err) => {
            if (cb) { cb(); }
            if (err.status === 400) {
              toast.error(
                (
                  <div>
                    <h5>There was an error saving your payment account</h5>
                    <small>{get(err, 'data.message', 'Error occured')}</small>
                  </div>
                ),
                { autoClose: true }
              );
            }
          },
        });
      } else {
        if (cb) {
          cb(true);
        }
        toast.success(
          (
            <div>
              <h5>Payment method edited successfully!</h5>
            </div>
          ),
          { autoClose: true }
        );
        setIsAddOrEditPaymentModalVisible(false);
      }
    }
  };

  const {
    loansList,
    loanDetail,
    loanTerm,
    collateralSummary,
    collateralRequired,
    paymentsList,
    paymentMethods,
    autopay,
    loanCharges,
    channel,
    maxLateFee,
    notices,
  } = props;
  // The loans list has settled (finished loading) but yielded no active loan.
  // In that case there is nothing loan-scoped to wait on, so don't hold the
  // loader forever — render the dashboard shell instead of spinning.
  const loansSettledWithNoLoan = !props.loansList?.isLoading
    && !props.loansList?.activeLoanId
    && (props.loansList?.data != null || props.loansList?.error);
  const isDashboardLoading = !loansSettledWithNoLoan && (
    loanDetail?.isLoading
    || loanTerm?.isLoading
    || (!loanDetail?.data && !loanDetail?.error)
  );

  if (isDashboardLoading) {
    return (
      <div className="page-dashboard">
        <Container fluid>
          <Loader className="height-2" />
        </Container>
      </div>
    );
  }

  // Determine loan scenario based on API data
  const loanDetailData = get(loanDetail, 'data.0', {});
  const isCrypto = resolveIsCryptoLoan(loansList);
  const isAutopayUnavailable = isOnlineAutopayUnavailable(loanTerm, loansList);
  const isLiquidationSatisfied = isLiquidationSatisfiedLoan(loanDetailData);
  const customerDetail = JSON.parse(sessionStorage.getItem('customerDetail'));
  const selectedLoan = loansList?.data?.find((loan) => String(loan?.loanId || '') === String(loansList?.activeLoanId || '')) || {};
  const fullLiquidation = get(collateralSummary, 'data.fullLiquidation');
  const hasFullLiquidation = Boolean(fullLiquidation);
  const netProceeds = Number(get(fullLiquidation, 'netProceeds'));
  const isDeficiencyCollection = !isLiquidationSatisfied && isDeficiencyBalanceLoan(loanDetailData);
  const isDeficiency = isDeficiencyCollection || (
    !isLiquidationSatisfied
    && hasFullLiquidation
    && Number(get(loanDetailData, 'principalBalance', 0)) > 0
  );
  // Priority: deficiency collection, then liquidation, then pending payoff.
  // Each view shows only when every higher-priority state does not apply.
  // Pending payoff shows only when tags include Pending Payoff.
  const showLiquidation = hasFullLiquidation && !isDeficiencyCollection;
  const isPendingPayoff = !isDeficiencyCollection
    && !showLiquidation
    && isPendingPayoffLoan(loanDetailData);
  const liquidationSummary = showLiquidation
    ? {
      liquidationDate: get(fullLiquidation, 'liquidationDate', null),
      collateralLiquidated: get(fullLiquidation, 'collateralLiquidated', null),
      assetType: get(collateralSummary, 'data.coin', null),
      liquidationProceeds: Number.isFinite(netProceeds) ? netProceeds : null,
    }
    : null;
  const pendingPayoffCollateralHeld = getCollateralSummaryHeldDisplay(
    get(collateralSummary, 'data')
  ).collateralHeld;
  const applicationId = get(selectedLoan, 'externalId')
    || get(loanDetailData, 'externalId')
    || '-';
  const isScraActive = get(selectedLoan, 'exclusions.isScraActive', false);
  const isScraWaiverSigned = get(selectedLoan, 'exclusions.isScraWaiverSigned', false);
  const isBankruptcy = get(selectedLoan, 'exclusions.isBankruptcy', false);
  const isDeceased = get(selectedLoan, 'exclusions.isDeceased', false);
  const allpaymentmethods = paymentMethods.data
    ? normalizePaymentAccounts(paymentMethods.data)
    : [];

  const primaryPaymentMethod = paymentMethods.data
    ? derivePrimaryPaymentMethod(paymentMethods.data)
    : '';

  const {
    pastDue,
    showCryptoPastDueStatus,
    showDpdPartialLiquidationBanner,
    showAccountStatus,
    isLtvAboveContractual,
    isLtvMarginCall,
    isLtvCriticalWarning,
    lowerLtvData,
    liquidationAfterDays,
    marginCallPct,
    contractualLtvPct,
    currentLtvPct,
  } = resolveDashboardCryptoState({
    isCrypto,
    loanDetail,
    loanTerm,
    collateralSummary,
    collateralRequired,
  });

  const maturityWarning = resolveMaturityWarningState({ loanTerm, loanDetail });

  const cryptoWelcomeHeader = isCrypto && !isDeficiencyCollection && !showLiquidation && !showDpdPartialLiquidationBanner && !isPendingPayoff
    ? getCryptoWelcomeHeaderContent({
      showCryptoPastDueStatus,
      isLtvCriticalWarning,
      isLtvMarginCall,
      isLtvAboveContractual,
      daysPastDue: pastDue,
      currentLtvDisplay: formatLtvMetricDisplay(get(collateralSummary, 'data.currentLtv')),
      liquidationAfterDays,
      currentLtvPct,
      marginCallPct,
      contractualLtvPct,
      maturityWarning,
    })
    : null;

  const visitedDashboard = sessionStorage.getItem('visitedDashboard');

  // data parsing
  const payments = get(paymentsList, 'data');
  const customerName = `${get(customerDetail, 'firstName')} ${get(customerDetail, 'lastName')}`;
  const principalAmount = Number(get(loanTerm, 'data.principalAmount', 0));
  const principalBalance = Number(get(loanDetail, 'data.0.principalBalance', 0));
  const rawPayoff = get(loanDetailData, 'payoff');
  const payoffAmount = Number(rawPayoff);
  const deficiencyPayoff = isDeficiency && rawPayoff != null && rawPayoff !== '' && Number.isFinite(payoffAmount)
    ? payoffAmount
    : null;
  const payoffDisplayParts = deficiencyPayoff != null
    ? formatCurrency(deficiencyPayoff, 2).split('.')
    : ['0', '00'];
  const displayBalanceParts = isDeficiency
    ? payoffDisplayParts
    : formatCurrency(principalBalance, 2).split('.');
  const settlementAmount = Number(get(loanDetail, 'data.0.settlement.settlementAmount', 0));
  const settlementPaidAmount = Number(get(loanDetail, 'data.0.settlement.totalAmountPaid', 0));
  const nextPaymentAmount = get(loanDetail, 'data.0.nextPaymentAmount', 0) && formatCurrency(get(loanDetail, 'data.0.nextPaymentAmount', 0), 2).split('.');
  const dashboardNextPaymentAmount = isDeficiency ? payoffDisplayParts : nextPaymentAmount;
  const pastDueAmount = formatCurrency(get(loanDetail, 'data.0.amountDue'), 2).split('.');
  const dueFees = Number(get(loanCharges, 'data.unpaidCharges'));
  const totalDueAmount = get(loanDetail, 'data.0.totalDue');
  const totalDueDisplay = isDeficiency
    ? payoffDisplayParts
    : formatCurrency(Number(totalDueAmount || 0), 2).split('.');
  const nextPaymentDate = get(loanDetail, 'data.0.nextPaymentDate', '') ? moment(new Date(get(loanDetail, 'data.0.nextPaymentDate', ''))).format('MM/DD/YYYY').toString() : '-';
  const gracePeriodDeadline = pastDue > 0 ? moment().subtract(pastDue, 'days').add(16, 'days').format('MM/DD/YYYY') : nextPaymentDate;
  const lowerLtvPastDueAmount = pastDueAmount.length > 0
    ? `${pastDueAmount[0]}.${pastDueAmount[1]}`
    : '0.00';
  const lowerLtvDueFees = formatCurrency(dueFees, 2);
  const lowerLtvTotalAmount = formatCurrency(totalDueAmount || 0, 2);
  const deficiencyPayoffDisplay = deficiencyPayoff != null
    ? formatCurrency(deficiencyPayoff, 2)
    : null;
  const lowerLtvPayFromAccounts = allpaymentmethods.map((item) => {
    const type = Array.isArray(item.type) ? item.type[2] : item.type;
    const normalizedType = String(type).toLowerCase();

    return {
      value: `${item.paymentAccountId}-${normalizedType}`,
      title: `${capitalize(type)} ${['debit', 'credit'].includes(normalizedType) ? ' xxxx-xxxx-xxxx-' : ' xxxxxx'}${String(item.accountNumber).slice(-4)}`,
    };
  });
  const lowerLtvPaymentFormDefaults = {
    payFromAccounts: lowerLtvPayFromAccounts,
    primaryPaymentMethod,
    paymentDate: getCurrentDate(),
    totalDueAmount: deficiencyPayoffDisplay == null ? lowerLtvTotalAmount : deficiencyPayoffDisplay,
    pastDueAmount: deficiencyPayoffDisplay == null ? lowerLtvPastDueAmount : deficiencyPayoffDisplay,
    dueFees: deficiencyPayoffDisplay == null ? lowerLtvDueFees : '0.00',
    nextPaymentDueDate: nextPaymentDate === '-' ? '' : nextPaymentDate,
    paymentAmountOptions: deficiencyPayoffDisplay == null
      ? [
        {
          title: `Total Due - $${lowerLtvTotalAmount}`,
          value: '0',
        },
        {
          title: `Past Due (including fees) - $${formatCurrency(
            Number(floatUnmask(lowerLtvPastDueAmount)) + Number(floatUnmask(lowerLtvDueFees)),
            2
          )}`,
          value: '1',
        },
        {
          title: 'Other Amount',
          value: '2',
        },
      ]
      : [
        {
          title: `Payoff - $${deficiencyPayoffDisplay}`,
          value: '0',
        },
      ],
  };

  const handleLowerLtvMakePayment = (paymentData, onComplete) => {
    const {
      payFromAccount,
      paymentAmount,
      paymentDate,
      paymentAmountType,
      dueFees: paymentDueFees,
    } = paymentData;

    if (!payFromAccount || payFromAccount === 'addPaymentMethod' || paymentAmount == null || !paymentDate) {
      onComplete?.(false);
      return;
    }

    const selectedAccount = payFromAccount.split('-');
    const lowerLtvAccountType = (selectedAccount[1] || '').toLowerCase();
    const paymentMethodId = (lowerLtvAccountType === 'savings' || lowerLtvAccountType === 'checking') ? 4 : 3;
    let fee = paymentDueFees || formatCurrency(dueFees, 2);
    if (paymentAmountType === '2' || deficiencyPayoff != null) {
      fee = '0.00';
    }

    const paymentOptions = { closePaymentModal: false };
    const handlePaymentComplete = (success) => {
      if (success) {
        setIsLowerLtvModalOpen(false);
        if (loansList.activeLoanId) {
          props.fetchCollateralRequired(loansList.activeLoanId);
        }
      }
      onComplete?.(success);
    };

    if (moment().startOf('day').diff(moment(paymentDate)) === 0) {
      putMakePayment({
        paymentMethodId,
        amount: deficiencyPayoff != null ? deficiencyPayoff : Number(paymentAmount),
        fee: Number(floatUnmask(fee)),
        date: paymentDate,
        paymentAccountId: selectedAccount[0],
        isPayoff: false,
      }, handlePaymentComplete, paymentOptions);
      return;
    }

    putScheduleAutopay({
      name: 'Regular Payment',
      type: 'autopay.type.single',
      amountType: 'autopay.amountType.static',
      amount: deficiencyPayoff != null ? deficiencyPayoff : Number(paymentAmount),
      fee: Number(floatUnmask(fee)),
      applyDate: moment(paymentDate).format('YYYY-MM-DD'),
      processDate: moment(paymentDate).format('YYYY-MM-DD'),
      recurringFrequency: 'autopay.recurringFrequency.single',
      recurringPeriods: 1,
      primaryPaymentMethodId: selectedAccount[0],
    }, handlePaymentComplete, paymentOptions);
  };

  const activeloanId = loansList.activeLoanId;
  const scraWaiverPath = getScraWaiverPath(notices);

  let paid = isSettlementActive ? settlementPaidAmount : principalAmount - principalBalance;

  if (isSettlementActive) {
    if (paid > settlementAmount) {
      paid = settlementAmount;
    } else if (paid < 0) {
      paid = 0;
    }
  } else if (paid > principalAmount) {
    paid = principalAmount;
  } else if (paid < 0) {
    paid = 0;
  }

  // initialize graph
  const paymentData = [{ name: 'Payments', paid }];
  return (
    <div className={`page-dashboard ${visitedDashboard ? 'noAnimations' : ''}`}>
      <Container fluid>
        {isApplePayAvailable && !isBannerHidden ? (
          <Row>
            <Col>
              <ApplePayBanner onClose={() => setIsBannerHidden(true)} />
            </Col>
          </Row>
        ) : null}
        <Row className="mb-3 align-items-center">
          <Col className="text-center text-md-left">
            <h3 className="mb-0">
              Welcome,
              {' '}
              {customerName}
              !
            </h3>
            <small>
              <strong>Customer ID:&nbsp;</strong>
              {' '}
              {get(customerDetail, 'customId')}
            </small>
          </Col>
          <Col xs={12} md="auto" className="mt-3 mt-md-0">
            {isDeficiencyCollection ? null : isPendingPayoff ? (
              <Row>
                <Col xs="auto" className="pr-0">
                  <img src="/icons/consumer-status-current.svg" alt="Pending Payoff" />
                </Col>
                <Col>
                  <h5 className="mb-0">Paid in Full</h5>
                  <small>
                    Application ID:
                    {' '}
                    {applicationId}
                  </small>
                </Col>
              </Row>
            ) : isCrypto ? (
              showDpdPartialLiquidationBanner ? null : (
                cryptoWelcomeHeader ? (
                  <Row>
                    <Col xs="auto" className="pr-0">
                      <img
                        src={
                          cryptoWelcomeHeader.icon === 'critical'
                            ? '/icons/consumer-status-pastdue.svg'
                            : cryptoWelcomeHeader.icon === 'warning'
                              ? '/icons/consumer-status-warning.svg'
                              : cryptoWelcomeHeader.icon === 'info'
                                ? '/icons/consumer-status-info.svg'
                                : '/icons/consumer-status-pastdue.svg'
                        }
                        alt={cryptoWelcomeHeader.title}
                        className={
                          cryptoWelcomeHeader.icon === 'warning'
                            ? 'dashboard-welcome-status-icon dashboard-welcome-status-icon--warning'
                            : cryptoWelcomeHeader.icon === 'critical'
                              ? 'dashboard-welcome-status-icon dashboard-welcome-status-icon--critical'
                              : undefined
                        }
                      />
                    </Col>
                    <Col>
                      <h5 className="mb-0">{cryptoWelcomeHeader.title}</h5>
                      <small>
                        {cryptoWelcomeHeader.subtitle}
                        {cryptoWelcomeHeader.subtitleLine2 && (
                          <>
                            <br />
                            {cryptoWelcomeHeader.subtitleLine2}
                          </>
                        )}
                      </small>
                    </Col>
                  </Row>
                ) : showAccountStatus ? (
                  <Row>
                    <Col xs="auto" className="pr-0">
                      <img src="/icons/consumer-status-current.svg" alt="Current" />
                    </Col>
                    <Col>
                      <h5 className="mb-0">
                        Great work
                        {' '}
                        {customerName && customerName.split(' ')[0]}
                        !
                      </h5>
                      <small>You are up to date with your payments</small>
                    </Col>
                  </Row>
                ) : null
              )
            ) : pastDue > 0 ? (
              <Row>
                <Col xs="auto" className="pr-0">
                  <img src="/icons/consumer-status-pastdue.svg" alt="Past Due" />
                </Col>
                <Col>
                  <h5 className="mb-0">Call 888-502-8085 or Make A Payment</h5>
                  <small>To keep your account in good standing</small>
                </Col>
              </Row>
            ) : (
              <Row>
                <Col xs="auto" className="pr-0">
                  <img src="/icons/consumer-status-current.svg" alt="Current" />
                </Col>
                <Col>
                  <h5 className="mb-0">
                    Great work
                    {' '}
                    {customerName && customerName.split(' ')[0]}
                    !
                  </h5>
                  <small>You are up to date with your payments</small>
                </Col>
              </Row>
            )}
          </Col>
          <Col xs={12} md="auto" className="text-right justify-content-end ml-md-3 mt-3 mt-md-0">
            <div className="mb-0 input-inline pb-0 form-group">
              <UncontrolledDropdown inNavbar>
                <DropdownToggle
                  nav
                  caret
                  style={{
                    padding: '.675rem 1.125rem',
                    height: 'auto',
                    backgroundColor: 'white',
                    border: '2px solidrgb(235, 219, 219)',
                    color: '#495057',
                    fontSize: '1rem',
                    marginBottom: '0px !important',
                  }}
                  className="custom-dropdown-toggle form-control"
                >
                  {activeloanId && loansList.data
                    ? loansList.data.find((item) => String(item.loanId) === String(activeloanId))?.externalId || 'Select'
                    : 'Select'}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem end>Select</DropdownItem>
                  {loansList.data && loansList.data.map((item, index) => (
                    <DropdownItem
                      key={`loanlist_${index}`}
                      onClick={() => handleChange({ target: { value: item.loanId } })}
                      active={String(activeloanId) === String(item.loanId)}
                    >
                      {item.externalId}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </Col>
        </Row>
        {isCrypto && isScraActive && !isScraWaiverSigned && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-warning d-flex align-items-center" role="alert">
                <img src="/icons/consumer-status-warning.svg" alt="" className="mr-2" style={{ width: '24px', height: '24px' }} />
                <div>
                  <strong>SCRA Notice:</strong>
                  {' '}
                  Your account is protected under the Servicemembers Civil Relief Act. We cannot
                  liquidate your collateral without your written authorization. Your loan continues
                  to accrue interest during this period, and a decline in collateral value may
                  increase your loan-to-value ratio.
                  {' '}
                  <NavLink
                    to={scraWaiverPath}
                    className="alert-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Review &amp; Sign waiver →
                  </NavLink>
                </div>
              </div>
            </Col>
          </Row>
        )}
        {isCrypto && isBankruptcy && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-warning d-flex align-items-start" role="alert">
                <img src="/icons/consumer-status-warning.svg" alt="" className="mr-2" style={{ width: '24px', height: '55px' }} />
                <div>
                  <strong>Bankruptcy Proceeding Notice</strong>
                  <br />
                  An active bankruptcy proceeding has been reported on this account. Under the
                  automatic stay (11 USC §362), we cannot take collection actions or exercise
                  control over collateral. Interest continues to accrue, and collateral value
                  changes may affect your account status. If you have bankruptcy counsel, please
                  share their contact information. If this notice is in error, contact us.
                </div>
              </div>
            </Col>
          </Row>
        )}
        {isCrypto && isDeceased && (
          <Row className="mb-3">
            <Col>
              <div className="alert alert-warning d-flex align-items-start" role="alert">
                <img src="/icons/consumer-status-warning.svg" alt="" className="mr-2" style={{ width: '24px', height: '69px' }} />
                <div>
                  <strong>Account Holder Notice</strong>
                  <br />
                  We have been informed of the passing of the account holder. Please accept our
                  sincere condolences. Account activity is paused pending verification and direction
                  from the estate. Interest continues to accrue and collateral value may change
                  during this period. Contact us as executor, successor-in-interest, or to report
                  an error at
                  {' '}
                  <a href="tel:888-502-8082" className="alert-link">888-502-8082</a>
                  {' '}
                  or
                  {' '}
                  <a href="mailto:customerservice@servicingusa.com" className="alert-link">customerservice@servicingusa.com</a>
                  .
                </div>
              </div>
            </Col>
          </Row>
        )}
        <Row>
          <Col md={6} lg={4}>
            <Card inverse color="primary">
              <CardBody className="text-center">
                {isPendingPayoff ? (
                  <>
                    <div>
                      {
                        loanDetail.data
                          ? (
                            <h1 style={{ fontSize: '3rem' }}>
                              $
                              {displayBalanceParts[0]}
                              .
                              <small>{displayBalanceParts[1]}</small>
                            </h1>
                          )
                          : <Loader className="light height-100" />
                      }
                    </div>
                    <h6>PENDING PAYOFF</h6>
                    <small className="d-block mt-3">
                      We&apos;re confirming your final payment has cleared.
                    </small>
                  </>
                ) : (
                  <>
                    <div style={{ height: 57 }}>
                      {
                        loanDetail.data
                          ? (
                            <h1 style={{ fontSize: '3rem' }}>
                              $
                              {displayBalanceParts[0]}
                              .
                              <small>{displayBalanceParts[1]}</small>
                            </h1>
                          )
                          : <Loader className="light height-100" />
                      }
                    </div>
                    <h6>
                      {`${isSettlementActive ? 'Settlement ' : 'Principal '}Balance`}
                    </h6>
                    <Row className="mt-4">
                      <Col className="pr-0">
                        <Card className="pt-2 pb-2" style={{ height: '110px' }}>
                          {
                            loanDetail.data ? (
                              <>
                                <h6>Next Payment</h6>
                                {dashboardNextPaymentAmount ? (
                                  <h3>
                                    $
                                    {dashboardNextPaymentAmount[0]}
                                    .
                                    <small>{dashboardNextPaymentAmount[1]}</small>
                                  </h3>
                                ) : <h3>-</h3>}
                                <h6 className="mb-0">
                                  <span>
                                    Due by
                                    {' '}
                                    {loanDetail.data ? nextPaymentDate : '-'}
                                  </span>
                                </h6>
                              </>
                            ) : (
                              <Loader className="light height-100" />
                            )
                          }
                        </Card>
                      </Col>
                      <Col>
                        <Card
                          className={[
                            'pt-2 pb-2',
                            isCrypto && showCryptoPastDueStatus ? 'bg-white' : '',
                            isCrypto && showDpdPartialLiquidationBanner
                              ? 'dashboard-past-due-card--dpd-liquidation-window'
                              : '',
                            !isCrypto && pastDue > 0 ? 'bg-white' : '',
                          ].filter(Boolean).join(' ')}
                          style={{ height: '110px' }}
                        >
                          {
                              loanDetail.data ? (
                                <>
                                  <h6
                                    className={
                                      isCrypto && showDpdPartialLiquidationBanner
                                        ? 'dashboard-past-due-card__label'
                                        : (isCrypto ? showCryptoPastDueStatus : pastDue > 0) ? 'text-danger' : ''
                                    }
                                  >
                                    Total Due
                                  </h6>
                                  <h3
                                    className={
                                      isCrypto && showDpdPartialLiquidationBanner
                                        ? 'dashboard-past-due-card__amount'
                                        : (isCrypto ? showCryptoPastDueStatus : pastDue > 0) ? 'text-danger' : ''
                                    }
                                  >
                                    $
                                    {totalDueDisplay[0]}
                                    .
                                    <small
                                      className={
                                        isCrypto && showDpdPartialLiquidationBanner
                                          ? 'dashboard-past-due-card__cents'
                                          : (isCrypto ? showCryptoPastDueStatus : pastDue > 0) ? 'text-danger' : ''
                                      }
                                    >
                                      {totalDueDisplay[1]}
                                    </small>
                                  </h3>
                                  <h6
                                    className={
                                      isCrypto && showDpdPartialLiquidationBanner
                                        ? 'mb-0 dashboard-past-due-card__label'
                                        : `mb-0 ${(isCrypto ? showCryptoPastDueStatus : pastDue > 0) ? 'text-danger' : ''}`
                                    }
                                  >
                                    {(() => {
                                      const isPastDue = isCrypto ? showCryptoPastDueStatus : pastDue > 0;
                                      if (!isPastDue) return 'UP TO DATE';
                                      if (isCrypto && pastDue > 0) return `${pastDue} days overdue`;
                                      return 'DUE NOW';
                                    })()}
                                  </h6>
                                </>
                              ) : (
                                <Loader className="light height-100" />
                              )
                            }
                        </Card>
                      </Col>
                    </Row>
                    {isInGracePeriod(pastDue) && (
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: 14,
                        border: '2px solid rgba(255,255,255,0.35)',
                        background: 'rgba(255,255,255,0.14)',
                        color: '#ffffff',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        marginTop: 14,
                        marginBottom: 14,
                      }}
                    >
                      {`A $${formatCurrency(Number(lateFee || 0), 2)} late fee may be applied if the past due amount is not paid by ${gracePeriodDeadline}.${isAutoPayEnabled ? ' Contact us to reschedule autopay to avoid this fee.' : ''}`}
                    </div>
                    )}
                    {isDeficiency && (
                    <div
                      style={{
                        padding: '14px 16px',
                        borderRadius: 14,
                        border: '2px solid rgba(255,255,255,0.35)',
                        background: 'rgba(255,255,255,0.14)',
                        color: '#ffffff',
                        fontWeight: 600,
                        lineHeight: 1.35,
                        marginTop: 14,
                        marginBottom: 14,
                      }}
                    >
                      Following the liquidation of your collateral, your remaining balance is due and payable. Please remit payment promptly.
                    </div>
                    )}
                    {!isAutoPayEnabled && (
                      isAutopayUnavailable ? (
                        <AutopayUnavailableTooltip message={AUTOPAY_UNAVAILABLE_MESSAGE}>
                          <Button
                            color="light"
                            outline
                            className="w-100"
                            isDisabled
                            style={{ pointerEvents: 'none' }}
                          >
                            <img src="/icons/autopay.svg" alt="" className="mr-1" />
                            Set Up Autopay
                          </Button>
                        </AutopayUnavailableTooltip>
                      ) : (
                        <Button
                          isLoading={autopay.isLoading || isAutoPayLoading}
                          isDisabled={isDeficiency}
                          color="light"
                          outline
                          className="w-100 mb-1"
                          onClick={isDeficiency ? undefined : handleModal.bind(null, 'autopay')}
                        >
                          <img src="/icons/autopay.svg" alt="" className="mr-1" />
                          Set Up Autopay
                        </Button>
                      )
                    )}
                    <Button
                      isLoading={isMakePaymentLoading}
                      color="light"
                      className="w-100"
                      onClick={handleModal.bind(null, 'one-time')}
                    >
                      <img src="/icons/payment.svg" alt="" className="mr-1" />
                      Make A Payment
                    </Button>
                  </>
                )}
              </CardBody>
            </Card>

          </Col>
          <Col md={6} lg={8}>
            {isPendingPayoff ? (
              isCrypto && collateralSummary?.isLoading ? (
                <Loader className="height-2" />
              ) : (
                <PendingPayoffCard
                  collateralHeld={pendingPayoffCollateralHeld}
                  showCollateral={isCrypto}
                />
              )
            ) : isCrypto && showLiquidation ? (
              <LiquidationDetailsCard
                liquidationDate={liquidationSummary ? liquidationSummary.liquidationDate : null}
                collateralLiquidated={liquidationSummary ? liquidationSummary.collateralLiquidated : null}
                assetType={liquidationSummary ? liquidationSummary.assetType : null}
                liquidationProceeds={liquidationSummary ? liquidationSummary.liquidationProceeds : null}
                principalBalance={principalBalance}
              />
            ) : isCrypto ? (
              <CollateralSummaryCard
                loanId={loansList.activeLoanId}
                onLowerLtv={handleLowerLtvModal}
              />
            ) : (
              <Card>
                <CardHeader className="text-left">
                  {isSettlementActive ? 'Settlement ' : 'Loan '}
                  Tracker
                  <ButtonLink onClick={handleModal.bind(null, 'one-time')} className={`float-right ${pastDue > 0 ? 'text-danger' : ''}`} style={{ cursor: 'pointer' }}><span className="btn-link" style={{ textDecoration: 'none' }}>Make A Payment</span></ButtonLink>
                </CardHeader>
                <CardBody className="pt-2">
                  <ResponsiveContainer width="100%" height={30}>
                    <BarChart
                      data={paymentData}
                      layout="vertical"
                      margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                      height={15}
                      barSize={15}
                    >
                      <XAxis type="number" height={0} domain={[0, loanTerm.data ? (isSettlementActive ? settlementAmount : principalAmount) : 100]} />
                      <YAxis dataKey="name" type="category" width={0} />
                      <Bar dataKey="paid" stackId="a" fill="#04BC6C" background={{ fill: '#DBE3EB' }} radius={3} />
                    </BarChart>
                  </ResponsiveContainer>
                  <small className="float-left">
                    <strong>
                      $
                      {formatCurrency(isSettlementActive ? settlementPaidAmount : (principalAmount - principalBalance), 2)}
                      {' '}
                      paid
                      {!isSettlementActive ? ' in principal' : ''}
                    </strong>
                  </small>
                  <small className="float-right">
                    $
                    {formatCurrency(isSettlementActive ? settlementAmount : principalAmount, 2)}
                  </small>
                </CardBody>
              </Card>
            )}

            <Card className="mt-2">
              <CardHeader className="text-md-left">
                Last 5 Payments
                <NavLink to="/consumer/loans" onClick={handleClickAllPayments} className="d-block d-md-inline float-md-right">View All Payments</NavLink>
              </CardHeader>
              {
                    payments === undefined
                      ? <Loader className="height-2" />
                      : (
                        <Table className="mb-0" size="sm" responsive striped borderless>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Description</th>
                              <th>Status</th>
                              <th className="text-right">Amount</th>
                              <th className="reversal-reason-column">Reversal Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payments.length > 0 ? (
                              payments.slice(0, 5).map((item, index) => (
                                <tr
                                  key={index}
                                  className={item.reverseReason ? 'reversal-reason-row' : ''}
                                >
                                  <th scope="row">{moment(new Date(item.date)).format('MM/DD/YYYY').toString()}</th>
                                  <td>{resolvePaymentDescription(item)}</td>
                                  <td>{resolveCollateralDisplayValue(item.status)}</td>
                                  <td className="text-right">
                                    $
                                    {formatCurrency(item.amount, 2)}
                                  </td>
                                  <td className="reversal-reason-column">
                                    {item.reverseReason || '-'}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={5} align="center" className="pt-3 pb-3">
                                  No data
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      )
                  }
            </Card>

          </Col>
        </Row>
      </Container>

      <PaymentModal
        isOpen={modal}
        toggle={handleModal}
        nextPaymentAmount={nextPaymentAmount}
        nextPaymentAmt={nextPaymentAmt}
        nextPaymentDate={nextPaymentDate}
        pastDueAmount={pastDueAmount}
        dueFees={dueFees}
        totalDueAmount={totalDueAmount}
        deficiencyPayoff={deficiencyPayoff}
        disableAutopay={isDeficiency}
        paymentMethods={allpaymentmethods}
        putMakePayment={putMakePayment}
        putScheduleAutopay={putScheduleAutopay}
        toggleTermsModal={handleTermsModal}
        toggleLoanAgreementModal={handleLoanAgreementModal}
        toggleRecurringModal={handleRecurringModal}
        toggleCancelPaymentModal={handleCancelPaymentModal}
        activeTab={activeTab}
        primaryPaymentMethod={primaryPaymentMethod}
        fetchCurrentPayoffInfo={props.fetchCurrentPayoffInfo}
        fetchFuturePayoffInfo={props.fetchFuturePayoffInfo}
        addPaymentMethod={handleAddPaymentModal}
        channel={channel}
        nextPaymentApplyDate={nextPayment?.applyDate ?? nextPayment?.date ?? ''}
      />

      <AddPaymentModal
        isOpen={isAddOrEditPaymentModalVisible}
        toggle={handleAddPaymentModal}
        saveToken={savePaymentAccount}
        saveDebitCardToken={saveDebitCardToken}
        paymentModalType={paymentModalType}
        validateBankRoutingNumber={props.validateBankRoutingNumber}
      />

      <DisclosureModal
        isOpen={isDisclosureModalVisible}
        toggle={toggleDisclosureModal}
        scheduledPaymentDate={scheduledPaymentDate}
      />

      <TermsModal
        isOpen={isTermsModalVisible}
        fee={maxLateFee}
        toggle={handleTermsModal}
        channel={channel}
      />

      <LoansAgreementModal
        isOpen={isLoanAgreementModal}
        toggle={handleLoanAgreementModal}
      />

      <RecurringPaymentModal
        isOpen={isRecurringPaymentModal}
        toggle={handleRecurringModal}
      />

      <CancelPaymentModal
        isOpen={isCancelPaymentModal}
        toggle={handleCancelPaymentModal}
      />

      <PromoPayoffModal
        isOpen={isPromoPayoffModal}
        toggle={handlePayoffModal}
      />

      {isCrypto && !isPendingPayoff ? (
        <LowerLtvModal
          isOpen={isLowerLtvModalOpen}
          toggle={handleLowerLtvModal}
          lowerLtvData={lowerLtvData}
          paymentFormDefaults={lowerLtvPaymentFormDefaults}
          onMakePayment={handleLowerLtvMakePayment}
          collateralRequired={collateralRequired?.data}
          isCollateralRequiredLoading={collateralRequired?.isLoading}
          loanId={loansList.activeLoanId}
        />
      ) : null}
    </div>
  );
};

Dashboard.propTypes = {
  fetchPaymentsList: PropTypes.func.isRequired,
  selectLoanId: PropTypes.func.isRequired,
  putScheduleAutopay: PropTypes.func.isRequired,
  postDebitCardAccount: PropTypes.func.isRequired,
  fetchAutopayInfo: PropTypes.func.isRequired,
  postPaymentAccount: PropTypes.func.isRequired,
  fetchLoanTerm: PropTypes.func.isRequired,
  fetchLoanDetail: PropTypes.func.isRequired,
  fetchLoansList: PropTypes.func.isRequired,
  fetchCustomerDetail: PropTypes.func.isRequired,
  customerDetail: PropTypes.object,
  fetchPaymentMethods: PropTypes.func.isRequired,
  fetchTransactionsList: PropTypes.func.isRequired,
  makePayment: PropTypes.func.isRequired,
  fetchCurrentPayoffInfo: PropTypes.func.isRequired,
  fetchFuturePayoffInfo: PropTypes.func.isRequired,
  fetchAutopays: PropTypes.func.isRequired,
  fetchLoanCharges: PropTypes.func.isRequired,
  getBillingSetting: PropTypes.func.isRequired,
  loansList: PropTypes.object.isRequired,
  scheduledPaymentsList: PropTypes.array,
  loanDetail: PropTypes.object.isRequired,
  loanTerm: PropTypes.object.isRequired,
  collateralSummary: PropTypes.object,
  collateralRequired: PropTypes.object,
  fetchCollateralRequired: PropTypes.func.isRequired,
  fetchCollateralSummary: PropTypes.func.isRequired,
  fetchNotices: PropTypes.func.isRequired,
  paymentsList: PropTypes.object,
  paymentMethods: PropTypes.object.isRequired,
  autopay: PropTypes.object.isRequired,
  loanCharges: PropTypes.object.isRequired,
  notices: PropTypes.object,
  channel: PropTypes.string.isRequired,
  promoEligibility: PropTypes.string.isRequired,
  maxLateFee: PropTypes.string.isRequired,
  validateBankRoutingNumber: PropTypes.func.isRequired,
  fetchScheduledPayment: PropTypes.func.isRequired,
};

Dashboard.defaultProps = {
  scheduledPaymentsList: [],
  paymentsList: [],
  collateralSummary: {},
  collateralRequired: {},
  notices: {},
  customerDetail: {},
};

const mapStateToProps = (state) => ({
  loansList: state.dashboard.loansList,
  customerDetail: state.dashboard.customerDetail,
  loanDetail: state.dashboard.loanDetail,
  loanTerm: state.dashboard.loanTerm,
  collateralSummary: state.dashboard.collateralSummary,
  collateralRequired: state.dashboard.collateralRequired,
  transactionsList: state.dashboard.transactionsList,
  paymentsList: state.dashboard.paymentsList,
  allPayments: state.dashboard.paymentData,
  paymentMethods: state.dashboard.paymentMethods,
  makePaymentMessage: state.dashboard.makePaymentMessage,
  autopay: state.dashboard.autopay,
  loanCharges: state.dashboard.loanCharges,
  notices: state.dashboard.notices,
  scheduledPaymentsList: state.dashboard.scheduledPaymentsList.data,
  channel: state.dashboard.billingSetting && state.dashboard.billingSetting.data && state.dashboard.billingSetting.data.customFields ? state.dashboard.billingSetting.data.customFields.channel : '',
  promoEligibility: state.dashboard.billingSetting && state.dashboard.billingSetting.data && state.dashboard.billingSetting.data.customFields ? state.dashboard.billingSetting.data.customFields.promoEligibility : 'No',
  maxLateFee: state.dashboard.billingSetting && state.dashboard.billingSetting.data && state.dashboard.billingSetting.data.customFields ? state.dashboard.billingSetting.data.customFields.maxLateFee : null,
});

const mapDispatchToProps = {
  selectLoanId,
  fetchLoansList,
  fetchCustomerDetail,
  fetchLoanDetail,
  fetchPaymentsList,
  fetchTransactionsList,
  fetchDocuments,
  fetchLoanTerm,
  makePayment,
  fetchPaymentMethods,
  putScheduleAutopay: putScheduleAutopayMapped,
  fetchAutopayInfo,
  fetchScheduledPayment,
  fetchCurrentPayoffInfo,
  fetchFuturePayoffInfo,
  putPaymentAccount,
  postPaymentAccount,
  postDebitCardAccount: postDebitCardAccountAction,
  fetchAutopays,
  fetchLoanCharges,
  getBillingSetting,
  validateBankRoutingNumber: validateBankRoutingNumberAction,
  fetchCollateralRequired,
  fetchCollateralSummary,
  fetchNotices,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(Dashboard);
